// src/pages/home/HomeCore.jsx
import axios from "axios";

import { useNavigate } from "react-router-dom";

import Select from "react-select";

import { motion } from "framer-motion";

import toast, { Toaster } from "react-hot-toast";

import {
  FaBus,
  FaCalendarAlt,
  FaExchangeAlt,
  FaLongArrowAltRight,
  FaArrowRight,
  FaSearch,
} from "react-icons/fa";

import { FaClock } from "react-icons/fa";

import Footer from "../../components/Footer";

import { useState, useEffect, useRef } from "react";

import NoticesSection from "../../components/NoticesSection";
import WhatsNewSection from "../../components/WhatsNewSection";
import HolidaysSection from "../../components/HolidaysSection";

import HomeFaqWideSection from "../../components/HomeFaqWideSection";

import apiClient from "../../api";

import {
  PALETTE,
  SECTION_WRAP,
  SECTION_INNER,
  IOS_BAR_COLOR,
  whyChooseUsData,
} from "./parts/constants";

import { toLocalYYYYMMDD } from "./parts/dateUtils";

import { setOrCreateMeta } from "./parts/metaUtils";

import { buildLocationData } from "./parts/locationData";

import {
  LocationOption,
  LocationSingleValue,
  rbFilterOption,
  makeRbFilterSort,
  RedbusMenuList,
} from "./parts/selectParts";

import isRefVisible from "./parts/isRefVisible";

import getReadableDate from "./parts/getReadableDate";

import CalendarPopover from "./parts/calendar/CalendarPopover";

import MobileCalendarSheet from "./parts/calendar/MobileCalendarSheet";

import MobileLocationPicker from "./parts/pickers/MobileLocationPicker";

const HomeCore = () => {
  // ✅ store selected as objects for proper label display
  const [fromSel, setFromSel] = useState(null);
  const [toSel, setToSel] = useState(null);

  // ✅ keep current input value for match-first sorting
  const [fromInput, setFromInput] = useState("");
  const [toInput, setToInput] = useState("");

  const [fromMenuOpen, setFromMenuOpen] = useState(false);
  const [toMenuOpen, setToMenuOpen] = useState(false);

  const [date, setDate] = useState(toLocalYYYYMMDD(new Date()));
  const [allBuses, setAllBuses] = useState([]);

  const navigate = useNavigate();

  // ✅ HOME BACK GUARD
  // Home page should behave like the root page of the app.
  // Browser back from Home is trapped so users cannot return to old booking/search/ticket pages.
  const homeBackToastRef = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const guardState = {
      ...(window.history.state || {}),
      rb_home_guard: true,
    };

    try {
      window.history.replaceState(guardState, "", window.location.href);
      window.history.pushState({ rb_home_guard: true }, "", window.location.href);
    } catch {}

    const handleHomeBack = () => {
      try {
        window.history.pushState({ rb_home_guard: true }, "", window.location.href);
      } catch {}

      const now = Date.now();
      if (now - homeBackToastRef.current > 1600) {
        homeBackToastRef.current = now;
        toast("You are already on the Home page", {
          id: "home-back-blocked",
          icon: "🏠",
          duration: 1600,
        });
      }
    };

    window.addEventListener("popstate", handleHomeBack);

    return () => {
      window.removeEventListener("popstate", handleHomeBack);
    };
  }, []);

  // ✅ iPhone full-display + notch + colored status bar (like redBus)
  useEffect(() => {
    const isIOS =
      /iP(hone|od|ad)/.test(navigator.platform) ||
      (navigator.userAgent.includes("Mac") && "ontouchend" in document);

    try {
      // 1) viewport-fit=cover so content extends into the notch area
      let viewport = document.querySelector('meta[name="viewport"]');
      if (!viewport) {
        viewport = document.createElement("meta");
        viewport.name = "viewport";
        document.head.appendChild(viewport);
      }
      if (!/viewport-fit=cover/.test(viewport.content || "")) {
        viewport.content =
          (viewport.content ? viewport.content + ", " : "") +
          "viewport-fit=cover";
      }

      // 2) color the iOS status bar area (Safari uses theme-color)
      setOrCreateMeta("theme-color", IOS_BAR_COLOR);
      setOrCreateMeta("theme-color", IOS_BAR_COLOR, {
        media: "(prefers-color-scheme: light)",
      });
      setOrCreateMeta("theme-color", IOS_BAR_COLOR, {
        media: "(prefers-color-scheme: dark)",
      });

      // 3) If user adds to Home Screen, prefer light status bar background
      setOrCreateMeta("apple-mobile-web-app-capable", "yes");
      setOrCreateMeta("apple-mobile-web-app-status-bar-style", "default");
    } catch {}

    if (isIOS) document.documentElement.style.webkitTextSizeAdjust = "100%";
  }, []);

  // calendar popover state
  const [calOpen, setCalOpen] = useState(false);
  const desktopDateAnchorRef = useRef(null);
  const mobileDateAnchorRef = useRef(null);

  // ✅ NEW: scroll-to-search ref + helper (used by CTA buttons)
  const searchCardRef = useRef(null);
  const scrollToSearch = () => {
    if (!searchCardRef.current) return;
    searchCardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // mobile picker state
  const [mobilePickerOpen, setMobilePickerOpen] = useState(false);
  const [mobilePickerMode, setMobilePickerMode] = useState("from"); // 'from' | 'to'

  // recents (persist to localStorage)
  const [recent, setRecent] = useState(() => {
    try {
      return {
        from: JSON.parse(localStorage.getItem("rb_recent_from") || "[]"),
        to: JSON.parse(localStorage.getItem("rb_recent_to") || "[]"),
      };
    } catch {
      return { from: [], to: [] };
    }
  });

  const todayStr = toLocalYYYYMMDD(new Date());
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = toLocalYYYYMMDD(tomorrow);

  useEffect(() => {
    const fetchAllBuses = async () => {
      try {
        const res = await apiClient.get("/public/buses");
        setAllBuses(res.data);
      } catch (err) {
        console.error("Failed to fetch buses", err);
        toast.error("Failed to fetch bus locations.");
      }
    };
    fetchAllBuses();
  }, []);

  // ✅ Build cities + points maps
  const { cities, fromPointsByCity, toPointsByCity } = buildLocationData(allBuses);

  // ✅ Build react-select options (grouped)
  const cityOptions = cities.map((c) => ({
    value: c,
    label: c,
    kind: "city",
  }));

  const fromPointOptions = cities.flatMap((city) => {
    const pts = Array.from(fromPointsByCity.get(city) || []).sort((a, b) =>
      a.localeCompare(b)
    );
    return pts.map((p) => ({
      value: p,
      label: p,
      kind: "point",
      city,
      pointType: "from",
    }));
  });

  const toPointOptions = cities.flatMap((city) => {
    const pts = Array.from(toPointsByCity.get(city) || []).sort((a, b) =>
      a.localeCompare(b)
    );
    return pts.map((p) => ({
      value: p,
      label: p,
      kind: "point",
      city,
      pointType: "to",
    }));
  });

  const fromCityOptionsSorted = [...cityOptions].sort(makeRbFilterSort(fromInput));
  const toCityOptionsSorted = [...cityOptions].sort(makeRbFilterSort(toInput));

  const fromPointOptionsSorted = [...fromPointOptions].sort(makeRbFilterSort(fromInput));
  const toPointOptionsSorted = [...toPointOptions].sort(makeRbFilterSort(toInput));

  const fromOptions = [
    { label: "Cities", options: fromCityOptionsSorted },
    { label: "Boarding Points", options: fromPointOptionsSorted },
  ];

  const toOptions = [
    { label: "Cities", options: toCityOptionsSorted },
    { label: "Dropping Points", options: toPointOptionsSorted },
  ];

  // flat items for mobile picker
  const mobileFromItems = [...cityOptions, ...fromPointOptions];
  const mobileToItems = [...cityOptions, ...toPointOptions];

  const pushRecent = (key, value) => {
    setRecent((prev) => {
      const next = { ...prev };
      const list = [value, ...prev[key].filter((c) => c !== value)].slice(0, 5);
      next[key] = list;
      try {
        localStorage.setItem(
          key === "from" ? "rb_recent_from" : "rb_recent_to",
          JSON.stringify(list)
        );
      } catch {}
      return next;
    });
  };

  const handleSearch = () => {
    const dateToSearch = date || toLocalYYYYMMDD(new Date());
    const from = fromSel?.value || "";
    const to = toSel?.value || "";

    if (!from || !to || !dateToSearch) {
      toast.dismiss("home-search-missing-fields");
      toast.error("Select departure and destination to continue", {
        id: "home-search-missing-fields",
        duration: 3000,
      });
      return;
    }

    navigate(
      `/search-results?from=${encodeURIComponent(from)}&to=${encodeURIComponent(
        to
      )}&date=${encodeURIComponent(dateToSearch)}`
    );
  };

  const swapLocations = () => {
    const tempFrom = fromSel;
    setFromSel(toSel);
    setToSel(tempFrom);
  };

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      border: "none",
      boxShadow: "none",
      backgroundColor: "transparent",
      minHeight: "auto",
      height: "auto",
      cursor: "pointer",
      transition: "all 0.18s ease",
      opacity: state.isDisabled ? 0.6 : 1,
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0",
      gap: 0,
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
      color: PALETTE.textDark,
      fontSize: "18px",
      fontWeight: "600",
      fontFamily: "Inter, sans-serif",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6B7280",
      fontSize: "17px",
      fontWeight: "500",
      fontFamily: "Inter, sans-serif",
      margin: 0,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: PALETTE.textDark,
      fontSize: "18px",
      fontWeight: "700",
      fontFamily: "Inter, sans-serif",
      margin: 0,
      lineHeight: 1.2,
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: 4,
      color: "#9CA3AF",
      cursor: "pointer",
      ":hover": {
        color: PALETTE.primaryRed,
      },
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "20px",
      fontFamily: "Inter, sans-serif",
      backgroundColor: PALETTE.white,
      width: 440,
      maxWidth: "min(540px, calc(100vw - 24px))",
      boxShadow: "0 20px 60px -22px rgba(15,23,42,0.32)",
      border: "1px solid rgba(15,23,42,0.08)",
      overflow: "hidden",
      marginTop: 12,
      padding: 0,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
      maxHeight: 520,
      scrollbarWidth: "thin",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "rgba(216,78,85,0.14)"
        : state.isFocused
        ? "rgba(216,78,85,0.08)"
        : PALETTE.white,
      color: PALETTE.textDark,
      cursor: "pointer",
      padding: "14px 18px",
      transition:
        "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",
      borderBottom: "1px solid rgba(15,23,42,0.05)",
    }),
    group: (provided) => ({
      ...provided,
      paddingTop: 0,
      paddingBottom: 0,
    }),
    groupHeading: (provided) => ({
      ...provided,
      display: "none",
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      padding: "18px 16px",
      color: "#6B7280",
      fontSize: "14px",
      fontWeight: "600",
    }),
  };

  // handlers to open mobile picker
  const openMobilePicker = (mode) => {
    setMobilePickerMode(mode);
    setMobilePickerOpen(true);
  };

  const handleMobilePick = (value) => {
    // find selected option meta so desktop/more UI can show point->city label too
    const pool = mobilePickerMode === "from" ? mobileFromItems : mobileToItems;
    const picked = pool.find((x) => x.value === value) || {
      value,
      label: value,
      kind: "city",
    };

    if (mobilePickerMode === "from") {
      setFromSel(picked);
      pushRecent("from", value);
    } else {
      setToSel(picked);
      pushRecent("to", value);
    }
    setMobilePickerOpen(false);
  };

  return (
    <div
      className="min-h-screen min-h-[100svh] font-sans"
      style={{
        backgroundColor: PALETTE.bgLight,
        minHeight: "100dvh",
      }}
    >
      {/* Top safe-area */}
      <div
        className="lg:hidden"
        style={{
          height: "env(safe-area-inset-top)",
          backgroundColor: IOS_BAR_COLOR,
        }}
      />

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            borderRadius: "14px",
            background: "#ffffff",
            color: "#111827",
            border: "1px solid rgba(216,78,85,0.14)",
            boxShadow: "0 16px 36px -18px rgba(15,23,42,0.28)",
            padding: "12px 14px",
            fontSize: "14px",
            fontWeight: 600,
          },
          error: {
            iconTheme: {
              primary: PALETTE.primaryRed,
              secondary: "#ffffff",
            },
          },
        }}
      />

      {/* ===== Hero Section (desktop only) ===== */}
      <div
        className="hidden lg:block w-screen relative left-1/2 ml-[-50vw] overflow-hidden pb-20 lg:pb-40"
        style={{
          backgroundImage: "url('/images/wer.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-black/10 to-transparent"></div>

        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full blur-xl"
        />
        <motion.div
          animate={{ y: [0, 15, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-40 right-20 w-48 h-48 bg-white/5 rounded-full blur-2xl"
        />

        <div className="relative z-10 px-4 pt-16 sm:pt-24">
          <div className="max-w-6xl mx-auto text-center">
            <motion.div
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white mb-4 tracking-tight">
                Sri Lanka No:1 Bus Ticket Booking Platform
              </h1>
              <p className="text-lg sm:text-xl text-white/90 max-w-3xl mx-auto">
                Travel Smart with Routesbook.lk - Book Instantly
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* ===== Search Widget ===== */}
      <div className={`${SECTION_WRAP}`}>
        <div
          ref={searchCardRef}
          className={`${SECTION_INNER} relative z-20 mt-4 lg:-mt-32`}
        >
          {/* ==== MOBILE HEADER (brand on the right) ==== */}
          <div className="lg:hidden flex items-center justify-between pb-2 px-4 pt-4">
            <h2 className="text-xl font-bold" style={{ color: PALETTE.textDark }}>
              Get Your Bus Tickets
            </h2>
            <span
              className="text-lg font-extrabold tracking-tight"
              style={{
                color: PALETTE.primaryRed,
                fontFamily:
                  "'Mont', 'Montserrat', 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji'",
              }}
            >
              Routesbook
            </span>
          </div>

          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="bg-white border border-gray-300 rounded-xl lg:rounded-2xl lg:shadow-2xl"
          >
            {/* ----- DESKTOP VIEW ----- */}
            <div className="hidden lg:flex rounded-2xl overflow-hidden">
              <div
                className="relative flex-1 p-6 flex items-center border-r"
                style={{ borderColor: PALETTE.borderLight }}
              >
                <FaBus className="text-gray-400 mr-4 text-xl shrink-0" />
                <div className="w-full">
                  <label
                    className="block text-xs font-medium uppercase tracking-wider"
                    style={{ color: PALETTE.textLight }}
                  >
                    From
                  </label>
                  <Select
                    options={fromOptions}
                    value={fromSel}
                    menuIsOpen={fromMenuOpen}
                    onMenuOpen={() => setFromMenuOpen(true)}
                    onMenuClose={() => setFromMenuOpen(false)}
                    onChange={(s) => {
                      setFromSel(s || null);
                      const v = s?.value || "";
                      if (v) pushRecent("from", v);
                      setFromMenuOpen(false);
                    }}
                    placeholder="Select departure"
                    isClearable
                    onInputChange={(val) => {
                      setFromInput(val || "");
                      setFromMenuOpen(true);
                      return val;
                    }}
                    filterOption={rbFilterOption}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    components={{
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null,
                      Option: LocationOption,
                      SingleValue: LocationSingleValue,
                      MenuList: RedbusMenuList("from"),
                    }}
                    recent={recent}
                    closeMenuOnSelect={true}
                  />
                </div>

                <div className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 z-20">
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 180 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={swapLocations}
                    className="bg-white p-2 rounded-full shadow-lg transition-all duration-200"
                    style={{ border: `2px solid ${PALETTE.borderLight}` }}
                    title="Swap locations"
                  >
                    <FaExchangeAlt style={{ color: PALETTE.textLight }} />
                  </motion.button>
                </div>
              </div>

              <div
                className="relative flex-1 p-6 flex items-center border-r"
                style={{ borderColor: PALETTE.borderLight }}
              >
                <FaBus className="text-gray-400 mr-4 text-xl shrink-0" />
                <div className="w-full">
                  <label
                    className="block text-xs font-medium uppercase tracking-wider"
                    style={{ color: PALETTE.textLight }}
                  >
                    To
                  </label>
                  <Select
                    options={toOptions}
                    value={toSel}
                    menuIsOpen={toMenuOpen}
                    onMenuOpen={() => setToMenuOpen(true)}
                    onMenuClose={() => setToMenuOpen(false)}
                    onChange={(s) => {
                      setToSel(s || null);
                      const v = s?.value || "";
                      if (v) pushRecent("to", v);
                      setToMenuOpen(false);
                    }}
                    placeholder="Select destination"
                    isClearable
                    onInputChange={(val) => {
                      setToInput(val || "");
                      setToMenuOpen(true);
                      return val;
                    }}
                    filterOption={rbFilterOption}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    components={{
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null,
                      Option: LocationOption,
                      SingleValue: LocationSingleValue,
                      MenuList: RedbusMenuList("to"),
                    }}
                    recent={recent}
                    closeMenuOnSelect={true}
                  />
                </div>
              </div>

              {/* DATE (Desktop) */}
              <div className="flex-1 p-6 flex items-center relative">
                <FaCalendarAlt className="text-gray-400 mr-4 text-xl shrink-0" />
                <div className="w-full">
                  <label
                    className="block text-xs font-medium uppercase tracking-wider"
                    style={{ color: PALETTE.textLight }}
                  >
                    Date of Journey
                  </label>
                  <div
                    ref={desktopDateAnchorRef}
                    onClick={() => setCalOpen(true)}
                    className="cursor-pointer"
                  >
                    <span
                      className="text-lg font-medium"
                      style={{ color: PALETTE.textDark }}
                    >
                      {getReadableDate(date)}
                    </span>
                  </div>
                  <div className="mt-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDate(todayStr);
                      }}
                      className="text-xs font-medium mr-3"
                      style={{
                        color:
                          date === todayStr
                            ? PALETTE.primaryRed
                            : PALETTE.accentBlue,
                      }}
                    >
                      Today
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDate(tomorrowStr);
                      }}
                      className="text-xs font-medium"
                      style={{
                        color:
                          date === tomorrowStr
                            ? PALETTE.primaryRed
                            : PALETTE.accentBlue,
                      }}
                    >
                      Tomorrow
                    </button>
                  </div>
                </div>

                <CalendarPopover
                  anchorRef={desktopDateAnchorRef}
                  open={calOpen && isRefVisible(desktopDateAnchorRef)}
                  value={date}
                  minDateString={todayStr}
                  onChange={setDate}
                  onClose={() => setCalOpen(false)}
                />
              </div>

              <div className="p-4 flex items-center">
                {/* ✅ FLAT RED PILL (desktop) */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSearch}
                  className="font-heading w-full lg:w-auto flex items-center justify-center gap-2 text-white font-semibold tracking-wide px-6 py-3 rounded-full shadow-none"
                  style={{ backgroundColor: PALETTE.primaryRed }}
                >
                  <FaSearch />
                  SEARCH BUSES
                </motion.button>
              </div>
            </div>

            {/* ----- MOBILE VIEW (COMPACT) ----- */}
            <div className="lg:hidden">
              <div className="relative">
                {/* FROM */}
                <div className="p-3">
                  <div
                    className="flex items-center gap-3 pb-3 border-b"
                    style={{ borderColor: PALETTE.borderLight }}
                  >
                    <FaBus
                      className="shrink-0 text-base"
                      style={{ color: PALETTE.textLight }}
                    />
                    <div className="w-full">
                      <label className="block text-[11px] font-medium text-gray-500">
                        From
                      </label>
                      <button
                        type="button"
                        onClick={() => openMobilePicker("from")}
                        className="w-full text-left py-1.5"
                      >
                        <span
                          className={`text-base ${
                            fromSel ? "font-semibold text-gray-900" : "text-gray-400"
                          }`}
                        >
                          {fromSel?.label || "Select departure"}
                        </span>
                        {fromSel?.kind === "point" && fromSel?.city ? (
                          <div className="text-xs text-gray-500">
                            {fromSel.city}
                          </div>
                        ) : null}
                      </button>
                    </div>
                  </div>

                  {/* TO */}
                  <div className="flex items-center gap-3 pt-3">
                    <FaBus
                      className="shrink-0 text-base"
                      style={{ color: PALETTE.textLight }}
                    />
                    <div className="w-full">
                      <label className="block text-[11px] font-medium text-gray-500">
                        To
                      </label>
                      <button
                        type="button"
                        onClick={() => openMobilePicker("to")}
                        className="w-full text-left py-1.5"
                      >
                        <span
                          className={`text-base ${
                            toSel ? "font-semibold text-gray-900" : "text-gray-400"
                          }`}
                        >
                          {toSel?.label || "Select destination"}
                        </span>
                        {toSel?.kind === "point" && toSel?.city ? (
                          <div className="text-xs text-gray-500">{toSel.city}</div>
                        ) : null}
                      </button>
                    </div>
                  </div>
                </div>

                {/* SWAP BUTTON */}
                <div className="absolute top-1/2 right-3 transform -translate-y-1/2 z-10">
                  <motion.button
                    whileTap={{ scale: 0.9, rotate: 180 }}
                    onClick={swapLocations}
                    className="bg-white p-2.5 rounded-full shadow-md transition-all duration-200"
                    style={{ border: `1px solid ${PALETTE.borderLight}` }}
                    title="Swap locations"
                  >
                    <FaExchangeAlt
                      className="text-base rotate-90"
                      style={{ color: PALETTE.textLight }}
                    />
                  </motion.button>
                </div>
              </div>

              {/* DATE (Mobile) */}
              <div
                className="flex items-center gap-3 p-3 border-t relative"
                style={{ borderColor: PALETTE.borderLight }}
              >
                <FaCalendarAlt
                  className="shrink-0 text-base"
                  style={{ color: PALETTE.textLight }}
                />
                <div
                  ref={mobileDateAnchorRef}
                  onClick={() => setCalOpen(true)}
                  className="flex-grow cursor-pointer"
                >
                  <label className="block text-[11px] font-medium text-gray-500">
                    Date of Journey
                  </label>
                  <span
                    className="text-base font-semibold"
                    style={{ color: PALETTE.textDark }}
                  >
                    {getReadableDate(date)}
                  </span>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDate(todayStr);
                    }}
                    className={`text-xs font-semibold ${
                      date === todayStr ? "text-red-500" : "text-gray-600"
                    }`}
                  >
                    Today
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDate(tomorrowStr);
                    }}
                    className={`text-xs font-semibold ${
                      date === tomorrowStr ? "text-red-500" : "text-gray-600"
                    }`}
                  >
                    Tomorrow
                  </button>
                </div>
              </div>

              {/* SEARCH BUTTON */}
              <div
                className="p-3 border-t"
                style={{ borderColor: PALETTE.borderLight }}
              >
                {/* ✅ FLAT RED PILL (mobile) */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSearch}
                  className="font-heading w-full flex items-center justify-center gap-2 text-white font-semibold tracking-wide py-3 rounded-full shadow-none"
                  style={{ backgroundColor: PALETTE.primaryRed }}
                >
                  <FaSearch />
                  Search Buses
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ===== Upcoming Holidays ===== */}
      <div>
        <HolidaysSection />
      </div>

      {/* ===== Offers / Notices ===== */}
      <div className="-mt-8 sm:-mt-10 md:-mt-12 lg:-mt-16">
        <NoticesSection />
      </div>

      {/* ===== What's New ===== */}
      <WhatsNewSection />

      {/* ===== Popular Routes (UPDATED to match premium UI) ===== */}
      <div className={`${SECTION_WRAP}`}>
        <section className="w-screen relative left-1/2 ml-[-50vw] overflow-hidden">
          <div
            className="relative py-16 md:py-20"
            style={{
              background:
                "radial-gradient(circle at top left, rgba(58,134,255,0.12) 0%, transparent 34%), radial-gradient(circle at top right, rgba(216,78,85,0.14) 0%, transparent 32%), linear-gradient(180deg, rgba(248,250,252,1) 0%, rgba(255,247,247,0.75) 48%, rgba(255,255,255,1) 100%)",
            }}
          >
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-12 left-10 w-48 h-48 rounded-full blur-3xl"
              style={{ backgroundColor: "rgba(58,134,255,0.16)" }}
            />
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-20 right-12 w-56 h-56 rounded-full blur-3xl"
              style={{ backgroundColor: "rgba(216,78,85,0.16)" }}
            />

            <div className={`${SECTION_INNER} relative z-10`}>
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
                <div>
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-extrabold mb-3"
                    style={{
                      backgroundColor: "rgba(216,78,85,0.10)",
                      color: PALETTE.primaryRed,
                    }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-current" />
                    Most booked this week
                  </div>
                  <h2
                    className="text-3xl md:text-4xl font-extrabold tracking-tight"
                    style={{ color: PALETTE.textDark }}
                  >
                    Popular Routes
                  </h2>
                  <p
                    className="mt-2 text-base md:text-lg max-w-2xl"
                    style={{ color: PALETTE.textLight }}
                  >
                    Explore high-demand routes across Sri Lanka with faster seat booking and clear journey details.
                  </p>
                </div>

                <button
                  onClick={scrollToSearch}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border bg-white shadow-[0_12px_30px_-20px_rgba(15,23,42,0.35)] hover:shadow-[0_18px_38px_-22px_rgba(15,23,42,0.45)] transition"
                  style={{ borderColor: PALETTE.borderLight }}
                >
                  <span className="text-sm font-bold text-gray-900">Search Buses</span>
                  <FaArrowRight style={{ color: PALETTE.primaryRed }} />
                </button>
              </div>

              {/* Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { from: "Colombo", to: "Matara", tag: "Express", eta: "2h 30m", badge: "Top", buses: "12 buses/day", price: "Rs. 1,250" },
                  { from: "Colombo", to: "Galle", tag: "Highway", eta: "2h 00m", badge: "Hot", buses: "15 buses/day", price: "Rs. 1,100" },
                  { from: "Kandy", to: "Colombo", tag: "A/C", eta: "3h 00m", badge: "Fast", buses: "10 buses/day", price: "Rs. 1,450" },
                  { from: "Colombo", to: "Jaffna", tag: "Night", eta: "8h 30m", badge: "Long", buses: "8 buses/day", price: "Rs. 2,800" },
                  { from: "Colombo", to: "Badulla", tag: "Scenic", eta: "6h 30m", badge: "View", buses: "7 buses/day", price: "Rs. 2,200" },
                  { from: "Colombo", to: "Trincomalee", tag: "A/C", eta: "6h 00m", badge: "New", buses: "6 buses/day", price: "Rs. 2,350" },
                ].map((r, idx) => (
                  <motion.button
                    key={idx}
                    whileHover={{ y: -6 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() =>
                      navigate(
                        `/search-results?from=${encodeURIComponent(
                          r.from
                        )}&to=${encodeURIComponent(r.to)}&date=${todayStr}`
                      )
                    }
                    className="text-left group bg-white/90 backdrop-blur rounded-3xl border border-white shadow-[0_18px_45px_-28px_rgba(15,23,42,0.45)] hover:shadow-[0_24px_60px_-30px_rgba(15,23,42,0.55)] transition overflow-hidden"
                  >
                    <div
                      className="h-1.5 w-full"
                      style={{
                        background:
                          "linear-gradient(90deg, rgba(216,78,85,1) 0%, rgba(236,72,153,0.95) 48%, rgba(58,134,255,1) 100%)",
                      }}
                    />

                    <div className="p-6">
                      <div className="flex items-center justify-between gap-3">
                        <span
                          className="text-xs font-extrabold px-3 py-1.5 rounded-full"
                          style={{
                            backgroundColor: "rgba(216,78,85,0.10)",
                            color: PALETTE.primaryRed,
                          }}
                        >
                          {r.badge} Route
                        </span>

                        <span className="text-xs font-extrabold text-gray-400 uppercase tracking-wide">
                          {r.tag}
                        </span>
                      </div>

                      <div className="mt-6 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                            From
                          </div>
                          <div className="mt-1 text-xl font-black text-gray-950 truncate">
                            {r.from}
                          </div>
                        </div>

                        <div className="flex flex-1 items-center justify-center min-w-[80px]">
                          <span className="h-[2px] flex-1 bg-gray-200 rounded-full" />
                          <span
                            className="mx-2 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                            style={{
                              backgroundColor: "rgba(216,78,85,0.10)",
                              color: PALETTE.primaryRed,
                            }}
                          >
                            <FaLongArrowAltRight className="text-xl transition group-hover:translate-x-0.5" />
                          </span>
                          <span className="h-[2px] flex-1 bg-gray-200 rounded-full" />
                        </div>

                        <div className="min-w-0 text-right">
                          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                            To
                          </div>
                          <div className="mt-1 text-xl font-black text-gray-950 truncate">
                            {r.to}
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 grid grid-cols-2 gap-3">
                        <div className="rounded-2xl bg-gray-50 border border-gray-100 px-3 py-3">
                          <div className="flex items-center gap-2 text-sm font-bold text-gray-700">
                            <FaClock className="opacity-70" />
                            {r.eta}
                          </div>
                          <div className="mt-1 text-xs text-gray-400 font-semibold">
                            Duration
                          </div>
                        </div>

                        <div className="rounded-2xl bg-gray-50 border border-gray-100 px-3 py-3 text-right">
                          <div className="text-sm font-bold text-gray-700">
                            {r.buses}
                          </div>
                          <div className="mt-1 text-xs text-gray-400 font-semibold">
                            Availability
                          </div>
                        </div>
                      </div>

                      <div className="mt-5 flex items-end justify-between gap-4">
                        <div>
                          <div className="text-xs text-gray-400 font-semibold">
                            Starting from
                          </div>
                          <div className="mt-1 text-lg font-black text-gray-950">
                            {r.price}
                          </div>
                        </div>

                        <span
                          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-extrabold text-white transition-all duration-300 group-hover:shadow-lg"
                          style={{ backgroundColor: PALETTE.primaryRed }}
                        >
                          Book now
                          <FaArrowRight className="transition group-hover:translate-x-1" />
                        </span>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ===== Why Choose Us ===== */}
      <div className={`${SECTION_WRAP}`}>
        <section className="w-screen relative left-1/2 ml-[-50vw] overflow-hidden">
          <div className="py-16 md:py-20 bg-white">
            <div className={`${SECTION_INNER}`}>
              <div className="text-center max-w-3xl mx-auto">
                <h2
                  className="text-3xl md:text-4xl font-extrabold tracking-tight"
                  style={{ color: PALETTE.textDark }}
                >
                  Why Choose Routesbook?
                </h2>
                <p
                  className="mt-3 text-base md:text-lg"
                  style={{ color: PALETTE.textLight }}
                >
                  Stress-free bus booking with clear seats, fast confirmation, and a smooth experience for every journey.
                </p>
              </div>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {whyChooseUsData.map((item, idx) => (
                  <motion.div
                    key={idx}
                    whileHover={{ y: -4 }}
                    className="bg-white rounded-2xl border border-gray-200 shadow-[0_8px_20px_-12px_rgba(0,0,0,0.18)] hover:shadow-[0_14px_30px_-16px_rgba(0,0,0,0.25)] transition p-6"
                  >
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center"
                      style={{
                        backgroundColor: "rgba(216,78,85,0.10)",
                        color: PALETTE.primaryRed,
                      }}
                    >
                      <item.icon className="text-2xl" />
                    </div>
                    <h3 className="mt-4 text-lg font-bold text-gray-900">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm text-gray-600">{item.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ✅ NEW: Wide RedBus-style FAQ section */}
      <HomeFaqWideSection palette={PALETTE} />

      <Footer />

      {/* ====== Mobile sheets / pickers (portals) ====== */}
      <MobileCalendarSheet
        open={calOpen && !isRefVisible(desktopDateAnchorRef)}
        value={date}
        minDateString={todayStr}
        onChange={setDate}
        onClose={() => setCalOpen(false)}
      />

      <MobileLocationPicker
        open={mobilePickerOpen}
        mode={mobilePickerMode}
        items={mobilePickerMode === "from" ? mobileFromItems : mobileToItems}
        recent={recent}
        onPick={handleMobilePick}
        onClose={() => setMobilePickerOpen(false)}
      />

      {/* Bottom safe-area */}
      <div
        className="lg:hidden"
        style={{
          height: "env(safe-area-inset-bottom)",
          backgroundColor: PALETTE.bgLight,
        }}
      />
    </div>
  );
};

export default HomeCore;

